<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { IconImage } from '@/shared/ui';
  import TableBorderColorIcon from '@/shared/assets/icons/TableBorderColorIcon.svg';
  import TableBorderWidthIcon from '@/shared/assets/icons/TableBorderWidthIcon.svg';
  import TableBorderStyleIcon from '@/shared/assets/icons/TableBorderStyleIcon.svg';
  import TableTrashIcon from '@/shared/assets/icons/TableTrashIcon.svg';
  import { useTableEditorStore } from '../../../../model/table-editor.store';
  import { useFormBuilderStore } from '../../../../model/form-builder.store';
  import { useCellAlignmentControl } from '../../../../model/useCellAlignmentControl';
  import type { BorderStyleType } from '../../../../lib/cell-border-utils';
  import type { FormElement } from '../../../../model/form.types';
  import ColorPickerButton from './shared/ColorPickerButton.vue';
  import BorderWidthPicker from './shared/BorderWidthPicker.vue';
  import BorderStylePicker from './shared/BorderStylePicker.vue';
  import AlignmentPicker from './shared/AlignmentPicker.vue';
  import MenuDropdownButton from './shared/MenuDropdownButton.vue';

  const emit = defineEmits<{
    (e: 'delete-table'): void;
  }>();

  const tableEditorStore = useTableEditorStore();
  const formBuilderStore = useFormBuilderStore();

  const borderWidthOpen = ref(false);
  const borderStyleOpen = ref(false);

  const tableBorderWidth = computed({
    get: () => tableEditorStore.activeTable?.properties.borderWidth ?? 1,
    set: (value: number) => {
      if (!tableEditorStore.activeTable) return;
      const table = tableEditorStore.activeTable;
      formBuilderStore.updateElement(table.id, {
        properties: {
          ...table.properties,
          borderWidth: value,
        },
      } as Partial<FormElement>);
    },
  });

  const borderStyle = computed({
    get: () => (tableEditorStore.activeTable?.properties.borderStyle ?? 'solid') as BorderStyleType,
    set: (value: BorderStyleType) => {
      if (value === 'none') return;
      if (!tableEditorStore.activeTable) return;
      const table = tableEditorStore.activeTable;
      formBuilderStore.updateElement(table.id, {
        properties: {
          ...table.properties,
          borderStyle: value,
        },
      } as Partial<FormElement>);
    },
  });

  const borderColor = computed({
    get: () => tableEditorStore.activeTable?.properties.borderColor ?? '#e3e3e3',
    set: (value: string) => {
      if (!tableEditorStore.activeTable) return;
      const table = tableEditorStore.activeTable;
      formBuilderStore.updateElement(table.id, {
        properties: {
          ...table.properties,
          borderColor: value,
        },
      } as Partial<FormElement>);
    },
  });

  const { alignmentOpen, alignment, currentAlignmentIcon, handleAlignmentChange } =
    useCellAlignmentControl({
      get: () =>
        tableEditorStore.activeTable?.properties.cells?.[0]?.[0]?.properties?.alignment ?? 'left',
      set: (value) => tableEditorStore.updateAllCellsProperty('alignment', value),
    });
</script>

<template>
  <div
    class="table-menu"
    @click.stop
  >
    <div class="menu-style-bar">
      <MenuDropdownButton
        v-model:open="borderWidthOpen"
        :icon-src="TableBorderWidthIcon"
        label="Espessura de bordas"
      >
        <BorderWidthPicker
          :model-value="tableBorderWidth"
          @update:model-value="tableBorderWidth = $event"
        />
      </MenuDropdownButton>

      <MenuDropdownButton
        v-model:open="borderStyleOpen"
        :icon-src="TableBorderStyleIcon"
        label="Estilo de bordas"
      >
        <BorderStylePicker
          :model-value="borderStyle"
          @update:model-value="borderStyle = $event"
        />
      </MenuDropdownButton>

      <ColorPickerButton
        :model-value="borderColor"
        tooltip="Cor das bordas"
        :icon-src="TableBorderColorIcon"
        :show-color-swatch="false"
        @update:model-value="borderColor = $event"
      />

      <MenuDropdownButton
        v-model:open="alignmentOpen"
        :icon-src="currentAlignmentIcon"
        label="Alinhamento"
      >
        <AlignmentPicker
          :model-value="alignment"
          @update:model-value="handleAlignmentChange"
        />
      </MenuDropdownButton>

      <div class="separator" />

      <button
        v-tippy="{
          content: 'Deletar tabela',
          placement: 'top',
          theme: 'custom',
          trigger: 'mouseenter',
          hideOnClick: true,
        }"
        type="button"
        class="menu-icon-btn danger"
        aria-label="Deletar tabela"
        @click="emit('delete-table')"
      >
        <IconImage
          :src="TableTrashIcon"
          alt="Deletar tabela"
        />
      </button>
    </div>
  </div>
</template>

<style scoped>
  .table-menu {
    display: flex;
    flex-direction: column;
  }

  .menu-style-bar {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px 8px;
  }

  .separator {
    width: 1px;
    height: 20px;
    background-color: var(--color-gray-02);
    margin: 0 4px;
  }

  .menu-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 4px;
    color: var(--color-gray-07);
    transition: background-color 0.15s ease;
  }

  .menu-icon-btn:hover {
    background-color: var(--color-gray-01);
  }

  .menu-icon-btn :deep(svg) {
    width: 20px;
    height: 20px;
  }

  .menu-icon-btn.danger {
    color: var(--color-red-01);
  }

  .menu-icon-btn.danger:hover {
    background-color: #fef2f2;
  }
</style>
